import dayjs from 'dayjs';
import { useContext } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { InfoBlock } from 'app/components/commons/InfoBlock/InfoBlock';
import type {
  InfoCategory,
  InfoSection,
} from 'app/components/commons/InfoBlock/InfoBlock';
import { useBedCategories } from 'app/hooks/data/rooms/useBedCategories';
import type { BookingDetails } from 'app/typings/core/admin/bookings/booking';
import {
  getExtraRows,
  getFreeIncludedPerNightRow,
  getIncludedPerOnceRows,
  getPaidIncludedPerNightRows,
  getRoomRows,
  mapPriceSubRows,
} from 'app/utils/core/bookingDetails/utils';

type Props = {
  bookingDetails: BookingDetails;
};

export const PackageDetails = ({ bookingDetails }: Props) => {
  const { currencyFormatter } = useContext(CurrencyContext);
  const { data: bedCategories } = useBedCategories();

  const payment = bookingDetails.payments[0];
  const isMultiNightBooking =
    dayjs(bookingDetails.checkOut)
      .startOf('day')
      .diff(dayjs(bookingDetails.checkIn).startOf('day'), 'day') > 1;

  const freePerNightRow = getFreeIncludedPerNightRow(bookingDetails);
  const paidPerNightRows = getPaidIncludedPerNightRows(
    bookingDetails,
    currencyFormatter
  );
  const perOnceRows = getIncludedPerOnceRows(bookingDetails, currencyFormatter);
  const extraRows = getExtraRows(bookingDetails, currencyFormatter);

  const hasPerNightRows =
    freePerNightRow !== null || paidPerNightRows.length > 0;
  const hasPerOnceRows = perOnceRows.length > 0;

  const includedCategories: InfoCategory[] = [
    ...(hasPerNightRows
      ? [
          {
            label: isMultiNightBooking ? 'Included every night' : undefined,
            rows: [
              ...(freePerNightRow
                ? [
                    {
                      label: freePerNightRow.label,
                      value: currencyFormatter(freePerNightRow.price),
                      bold: false,
                    },
                  ]
                : []),
              ...paidPerNightRows.map(({ label, price, subRows }) => ({
                label,
                value: currencyFormatter(price),
                bold: false,
                subRows: subRows
                  ? mapPriceSubRows(subRows, currencyFormatter)
                  : undefined,
              })),
            ],
          },
        ]
      : []),
    ...(hasPerOnceRows
      ? [
          {
            label: 'Included once',
            rows: perOnceRows.map(({ label, price, subRows }) => ({
              label,
              value: currencyFormatter(price),
              bold: false,
              subRows: subRows
                ? mapPriceSubRows(subRows, currencyFormatter)
                : undefined,
            })),
          },
        ]
      : []),
  ];

  const sections: InfoSection[] = [
    ...(includedCategories.length > 0
      ? [
          {
            title: 'Standard package',
            categories: includedCategories,
          },
        ]
      : []),
    ...(extraRows.length > 0
      ? [
          {
            title: 'Extras',
            rows: extraRows.map(
              ({
                label,
                unitPrice,
                price,
                subRows,
                showUnitPrice,
                isOriginals,
              }) => ({
                label: showUnitPrice
                  ? `${label} (${
                      isOriginals ? 'Originals • ' : ''
                    }${currencyFormatter(unitPrice)})`
                  : label,
                value: currencyFormatter(price),
                bold: false,
                subRows: subRows
                  ? mapPriceSubRows(subRows, currencyFormatter)
                  : undefined,
              })
            ),
          },
        ]
      : []),
    {
      title: 'Room',
      rows: getRoomRows(bookingDetails, bedCategories ?? []).map(
        ({ label, price, subRows }) => ({
          label,
          value: currencyFormatter(price),
          bold: false,
          subRows: subRows
            ? mapPriceSubRows(subRows, currencyFormatter)
            : undefined,
        })
      ),
    },
    {
      rows: [
        {
          label: 'Staycation fees',
          value: currencyFormatter(bookingDetails.payments[0].feesAmount),
        },
      ],
    },
    {
      rows: [
        {
          label: 'Total paid incl. VAT',
          value: currencyFormatter(payment?.totalAmount ?? 0),
          bold: true,
        },
        {
          label: 'Net commission amount excl.',
          value: currencyFormatter(payment?.commissionAmount ?? 0),
        },
      ],
    },
  ];

  return <InfoBlock title="Package details" sections={sections} />;
};
